export function groupCartItemsByLayoutProfile(cartItems) {
  const grouped = {};

  for (const item of cartItems) {
    const layoutId = item.layout; // or item.layoutEditor.id if that's how you store it
    const profile = item.keycapProfile; // or item.profileEditor.profile
    const quantity = item.quantity;

    if (!layoutId || !profile) {
      continue; // If layout or profile is missing, skip this item
    }

    const key = `${layoutId}|${profile}`;
    if (!grouped[key]) {
      grouped[key] = 0;
    }
    grouped[key] += quantity;
  }

  // Convert that object into an array of { layoutId, profile, quantity }
  return Object.keys(grouped).map((key) => {
    const [layoutId, profile] = key.split('|');
    return {
      layoutId,
      profile,
      quantity: grouped[key],
    };
  });
} // summarize and combine all cart items by layout and profile

function mapLayoutIdsToCartItems(cartItems) {
  const layoutMap = {};

  cartItems.forEach((item, idx) => {
    const lId = item.layout; // get the Layout _id of the current cart item

    if (!lId) return;

    if (!layoutMap[lId]) {
      layoutMap[lId] = []; // initialize layoutMap[lId]
    }

    layoutMap[lId].push({
      name: item.name,
      quantity: item.quantity,
      cartIndex: idx,
    });
  });

  return layoutMap;
} // This function builds a map that assigns all cart items to a layoutId (layoutId -> array of cart items that reference that layout. For example: { [layoutIdString]: [ { itemName, quantity, indexInCart }... ] })

export function buildSimpleStockFailureMessages(simpleStockData, cartItems, t) {
  const layoutMap = mapLayoutIdsToCartItems(cartItems);

  const messages = [];

  for (const entry of simpleStockData.insufficient) {
    const { layoutId, profile, needed, available } = entry; // get the stock info for the current entry of insufficient items
    const itemsForLayout = layoutMap[layoutId] || []; // Collect all cart items referencing this layout

    if (itemsForLayout.length === 0) {
      continue; // No cart item references it? Possibly an edge case or "Layout not found"
    }

    if (itemsForLayout.length === 1) {
      // If there's exactly 1 item referencing this layout:
      const singleItem = itemsForLayout[0];

      if (available === 0) {
        // if the available quantity is 0, the item/layout is sold out (no matter the quantity)
        messages.push(
          `${t('place-order-screen-simple-stock-failure-message-1.1')} ${
            singleItem.name
          } ${t(
            'place-order-screen-simple-stock-failure-message-1.2'
          )} ${profile} ${t(
            'place-order-screen-simple-stock-failure-message-1.3'
          )}`
        );
      } else {
        // if the available quantity is greater than 0, it means that the user has this item more than once in the cart
        messages.push(
          `${t('place-order-screen-simple-stock-failure-message-2.1')} ${
            singleItem.name
          } ${t(
            'place-order-screen-simple-stock-failure-message-2.2'
          )} ${profile} ${t(
            'place-order-screen-simple-stock-failure-message-2.3'
          )} ${needed}${t(
            'place-order-screen-simple-stock-failure-message-2.4'
          )} ${available} ${t(
            'place-order-screen-simple-stock-failure-message-2.5'
          )}`
        );
      }
    } else {
      // If multiple cart items reference the same layout
      let subMsg = itemsForLayout.map((it) => `${it.name}`).join(', ');

      messages.push(
        `${t(
          'place-order-screen-simple-stock-failure-message-3.1'
        )}\n${subMsg}. ${t(
          'place-order-screen-simple-stock-failure-message-3.2'
        )} ${needed}${t(
          'place-order-screen-simple-stock-failure-message-3.3'
        )} ${available}).`
      );
    }
  }

  return messages;
} // the error messages for the out of stock items /api/layouts/simple-stock-check reports

export function buildComplexStockFailureMessages(stockData, cartItems, t) {
  const layoutMap = mapLayoutIdsToCartItems(cartItems); // Build a map: layoutId -> array of cart items that reference that layout
  const messages = []; // We'll accumulate the error messages in an array

  // Step 1: Decide which array of insufficient items to provide the user with information:
  const {
    insufficientKeycaps,
    insufficientCardboardBoxes,
    insufficientFoams,
    insufficientTransferFilm,
  } = stockData;

  let list = insufficientKeycaps;
  let category = 'keycaps'; // default

  if (list.length === 0) {
    // if insufficientKeycaps is empty:
    list = insufficientCardboardBoxes; // set insufficientCardboardBoxes as the value of list
    category = 'boxes';
  }
  if (list.length === 0) {
    // if insufficientCardboardBoxes is empty:
    list = insufficientFoams; // set insufficientFoams as the value of list
    category = 'foams';
  }
  if (list.length === 0) {
    // if insufficientFoams is empty:
    list = insufficientTransferFilm; // set insufficientTransferFilm as the value of list
    category = 'film';
  }
  if (list.length === 0) return messages; // no problems at all

  const pushMessage = (text) => messages.push(text); // helper to push toast messages

  // Step 2: Render messages depending on the category
  if (category === 'keycaps') {
    // Loop over each insufficientKeycap at the top level
    for (const ic of list) {
      const { keycapName, profile, needed, available, layoutIds } = ic;

      let totalItemsInvolved = 0;
      let itemsDescriptions = [];

      layoutIds.forEach((layoutId) => {
        // for each layout that needs the insufficient keycap:
        const itemsForLayout = layoutMap[layoutId] || []; // get all items in the cart that are based on this layout
        totalItemsInvolved += itemsForLayout.length; // get the number of different cart items that are based on this layout

        itemsForLayout.forEach((itm) => {
          itemsDescriptions.push({
            layoutId,
            itemName: itm.name,
            itemQuantity: itm.quantity,
          });
        }); // add the description of each cart item that is based on this layout to the itemsDescriptions array
      });

      if (layoutIds.length === 1 && totalItemsInvolved === 1) {
        // If only 1 layout references the insufficient keycap and only 1 cart item references this layout:
        const { itemName, itemQuantity } = itemsDescriptions[0]; // get the description of the item (as there is only one layout that needs the insufficient keycap and there is only one item in the cart thet references this layout, there will be only one entry in itemsDescriptions and therefor we can access it by itemsDescriptions[0])

        if (itemQuantity === 1) {
          // If the quantity of this item in the cart is 1, we can definitly tell that this item is sold out:
          // This case should already be handled at the top of the createOrder function, by checking the stockQuantities of all layouts in the cart. If only one item/layout references this insufficient keycap and the quantity of this item is 1, the stockQuantities of this layout should be 0 and the createOrder function should already have been stopped.
          pushMessage(
            `${t(
              'place-order-screen-complex-stock-failure-message-1.1'
            )} ${itemName} ${t(
              'place-order-screen-complex-stock-failure-message-1.2'
            )} ${profile} ${t(
              'place-order-screen-complex-stock-failure-message-1.3'
            )}`
          );
        } else {
          // If the user wanted multiple quantity of this single item:
          // This case should also already be handled by the handleSimpleStockFailure function (but we still include it in the complex stock check in case the simple check did not handle this case for some reason)
          pushMessage(
            `${t(
              'place-order-screen-complex-stock-failure-message-2.1'
            )} ${itemName} ${t(
              'place-order-screen-complex-stock-failure-message-2.2'
            )} ${profile} ${t(
              'place-order-screen-complex-stock-failure-message-2.3'
            )} ${itemQuantity}${t(
              'place-order-screen-complex-stock-failure-message-2.4'
            )} ${available} ${t(
              'place-order-screen-complex-stock-failure-message-2.5'
            )}`
          );
        }
      } else {
        // More complex scenario: multiple different items/layouts in the cart need the same insufficient keycap and/or multiple items in the cart are based on the same layout that needs the insufficient keycap:
        const itemNamesString = itemsDescriptions
          .map((itm) => itm.itemName)
          .join(', ');

        pushMessage(
          `${t(
            'place-order-screen-complex-stock-failure-message-3.1'
          )} ${keycapName} ${t(
            'place-order-screen-complex-stock-failure-message-3.2'
          )} ${needed} ${t(
            'place-order-screen-complex-stock-failure-message-3.3'
          )} ${available} ${
            available === 1
              ? t('place-order-screen-complex-stock-failure-message-3.4')
              : t('place-order-screen-complex-stock-failure-message-3.5')
          } ${t(
            'place-order-screen-complex-stock-failure-message-3.6'
          )}\n${itemNamesString})`
        );
      }
    }
  } else {
    // handle messages for cardboard boxes, foams, or transfer film
    list.forEach(({ layoutIds }) => {
      // for each entry inside list (every entry is one stock item, e.g. CardboardBox in size "L" and CardboardBox in size "M")
      const affectedNames = new Set();

      layoutIds.forEach((layoutId) => {
        (layoutMap[layoutId] || []).forEach((c) => affectedNames.add(c.name));
      }); // Build the set of cart-item names that rely on the current stock item

      const namesArr = Array.from(affectedNames);
      const itemsStr = namesArr.join(', ');
      const isPlural = namesArr.length > 1;

      pushMessage(
        `${t('place-order-screen-complex-stock-failure-message-4.1')}` +
          `${t(
            'place-order-screen-complex-stock-failure-message-4.2'
          )} ${itemsStr}. ` +
          (isPlural
            ? t('place-order-screen-complex-stock-failure-message-4.3')
            : t('place-order-screen-complex-stock-failure-message-4.4'))
      );
    });
  }

  return messages;
} // the detailed error messages for the out of stock items /api/layouts/complex-stock-check reports
